import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { fetchDesignationsStart, fetchDesignationsSuccess, fetchDesignationsFailure } from '../../../redux/slices/designationsSlice';
import { 
  Box, 
  Typography, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Button,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  TablePagination,
  Chip,
  useMediaQuery,
  useTheme,
  CircularProgress,
  Alert
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import RefreshIcon from '@mui/icons-material/Refresh';
import { getDesignations } from '../../../Action/api';

const Designations = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch = useDispatch();
  
  const { data: designations, loading, error } = useSelector((state) => state.designations);
  
  const [filteredDesignations, setFilteredDesignations] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');

  useEffect(() => {
    if (designations.length === 0) {
      fetchDesignations();
    } else {
      setFilteredDesignations(designations);
    }
  }, []);

  useEffect(() => {
    setFilteredDesignations(designations);
  }, [designations]);

  const fetchDesignations = async () => {
    try {
      dispatch(fetchDesignationsStart());
      const data = await getDesignations();
      dispatch(fetchDesignationsSuccess(data));
    } catch (err) {
      dispatch(fetchDesignationsFailure('Failed to fetch designations. Please try again.'));
      console.error('Error fetching designations:', err);
    }
  };

  useEffect(() => {
    let filtered = designations.filter((designation) =>
      designation.desName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      designation.desId.toLowerCase().includes(searchTerm.toLowerCase()) ||
      designation.depId.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Apply sorting
    if (sortColumn) {
      filtered = [...filtered].sort((a, b) => {
        let aValue = a[sortColumn];
        let bValue = b[sortColumn];
        
        if (sortColumn === 'desName' || sortColumn === 'depId') {
          aValue = aValue.toLowerCase();
          bValue = bValue.toLowerCase();
        }
        
        if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
    }

    setFilteredDesignations(filtered);
    setPage(0);
  }, [searchTerm, designations, sortColumn, sortDirection]);

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedDesignations = filteredDesignations.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box sx={{ 
      p: { xs: 1, sm: 2 }, 
      maxWidth: { xs: '100vw', md: 'calc(100vw - 280px)' }, 
      mx: 'auto',
      width: '100%'
    }}>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'stretch', sm: 'center' }, 
        mb: 2,
        gap: { xs: 1, sm: 0 }
      }}>
        <Typography 
          variant={isMobile ? 'h6' : 'h5'} 
          component="h1" 
          sx={{ fontWeight: 600 }}
        >
          Designations Management
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button 
            variant="outlined" 
            startIcon={<RefreshIcon />}
            size="small"
            onClick={fetchDesignations}
            disabled={loading}
            sx={{ textTransform: 'none' }}
          >
            {!isMobile && 'Reload'}
          </Button>
          <Button 
            variant="contained" 
            startIcon={!isMobile && <AddIcon />}
            size="small"
            fullWidth={isMobile}
            sx={{ textTransform: 'none' }}
          >
            Add Designation
          </Button>
        </Box>
      </Box>

      <Paper sx={{ p: 2, mb: 2 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search by designation name, code, or department ID..."
          value={searchTerm}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      ) : (
        <>
      <TableContainer 
        component={Paper} 
        sx={{ 
          boxShadow: 2,
          overflowX: 'auto',
          width: '100%',
          '&::-webkit-scrollbar': {
            height: '8px',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#888',
            borderRadius: '4px',
          }
        }}
      >
        <Table size="small" sx={{ minWidth: { xs: 500, sm: 650 } }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.875rem' }}>S.No</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.875rem' }}>Designation Code</TableCell>
              <TableCell 
                sx={{ 
                  fontWeight: 700, 
                  fontSize: '0.875rem',
                  cursor: 'pointer',
                  userSelect: 'none',
                  '&:hover': { backgroundColor: '#e0e0e0' }
                }}
                onClick={() => handleSort('desName')}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  Designation Name
                  {sortColumn === 'desName' && (
                    sortDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                  )}
                </Box>
              </TableCell>
              <TableCell 
                sx={{ 
                  fontWeight: 700, 
                  fontSize: '0.875rem',
                  cursor: 'pointer',
                  userSelect: 'none',
                  '&:hover': { backgroundColor: '#e0e0e0' }
                }}
                onClick={() => handleSort('depId')}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  Department ID
                  {sortColumn === 'depId' && (
                    sortDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                  )}
                </Box>
              </TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.875rem' }}>Created At</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.875rem' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedDesignations.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    No designations found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginatedDesignations.map((designation, index) => (
                <TableRow 
                  key={designation.id}
                  sx={{ 
                    '&:hover': { backgroundColor: '#f9f9f9' },
                    '&:nth-of-type(even)': { backgroundColor: '#fafafa' }
                  }}
                >
                  <TableCell sx={{ fontSize: '0.813rem' }}>{page * rowsPerPage + index + 1}</TableCell>
                  <TableCell sx={{ fontSize: '0.813rem' }}>
                    <Chip label={designation.desId} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.813rem', fontWeight: 500 }}>{designation.desName}</TableCell>
                  <TableCell sx={{ fontSize: '0.813rem' }}>
                    <Chip label={designation.depId} size="small" color="info" />
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.813rem' }}>
                    {designation.createdAt ? new Date(designation.createdAt).toLocaleDateString('en-GB', {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    }) : '-'}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit">
                      <IconButton size="small" color="primary">
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" color="error">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component={Paper}
        sx={{ 
          mt: 1, 
          boxShadow: 1,
          '.MuiTablePagination-toolbar': {
            flexWrap: { xs: 'wrap', sm: 'nowrap' },
            justifyContent: 'center'
          },
          '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
            fontSize: { xs: '0.75rem', sm: '0.875rem' }
          }
        }}
        rowsPerPageOptions={[5, 10, 25, 50]}
        count={filteredDesignations.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage={isMobile ? 'Rows:' : 'Rows per page:'}
      />
        </>
      )}
    </Box>
  );
};

export default Designations;
